import React, { useState, useEffect, useMemo } from 'react';
import '../../../themes/global.scss';
import '../../../themes/home.scss';
import { useDispatch, useSelector } from 'react-redux';
import ReactSwitch from 'react-switch';
import moment from 'moment';
import { ClipLoader } from 'react-spinners';
import MobileAppModal from '../../../commonComponents/MobileAppModal';
import {
  editJob, fetchJob, getProfessionProcedures, getUserEducation,
} from '../../../actions';
import { Colors } from '../../../themes/colors';

import arrow from '../../../images/Right_arrow.png';

import ModalRatePopup from '../../ModalRatePopup/ModalRatePopup';
import ShowStartTime from '../../../commonComponents/showStartTime';
import BackButton from '../../../commonComponents/BackButton';
import CalendarPopup from '../../../commonComponents/CalendarPopup';
import Button from '../../../commonComponents/Button';
import { UserProfession, UserSubProfession } from '../../../enums/UserProfession';
import { EducationUtils } from '../../../utils/EducationUtils';
import {
  getShouldAllowOfficeToPostProcedure,
  getShouldUsingCredentialServiceEndpoint,
} from '../../../growthbook';
import { SelectableChips } from '../../../commonComponents/SelectableChips';
import { UserType } from '../../../enums/UserType';

const EditJobPostFunction = ({ match }) => {
  const dispatch = useDispatch();
  const job = useSelector((state) => state.job.job);
  const averageRating = useSelector((state) => state.job?.averageRating);
  const userEducation = useSelector((state) => state.user.userEducation);

  const specialtiesAndProcedures = userEducation?.DNT?.specialtiesAndProcedures;

  const {
    specialty,
    procedures,
    subProfession,
    professionName,
    profession,
  } = job || {};

  const [offerStartDate, setOfferStartDate] = useState(null);
  const [offerStartTime, setOfferStartTime] = useState(null);
  const [offerEndTime, setOfferEndTime] = useState(null);
  const [offerRate, setOfferRate] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showRatePopup, setShowRatePopup] = useState(false);
  const [showStartTime, setShowStartTime] = useState(false);
  const [content, setContent] = useState(null);
  const [resultFromModal, setResultFromModal] = useState(null);
  const [breakTime, setBreakTime] = useState(null);
  const [isBreakPaid, setIsBreakPaid] = useState(false);
  const [enableAutoFill, setEnableAutoFill] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [selectedProcedureIndices, setSelectedProcedureIndices] = useState([]);

  const availableProcedures = useMemo(() => EducationUtils.getProceduresOfProfession({
    specialtiesAndProcedures,
    profession: UserProfession.getNormalizedCode(profession),
    specialty,
  }), [specialty, profession, specialtiesAndProcedures]);

  const profRate = averageRating[job?.profession] || 0;

  // updates selected procedures when available procedures is available
  // or not empty
  //
  // this, to handle `userEducation` might still load when editing occurs
  useEffect(() => {
    if (availableProcedures && availableProcedures.length > 0
      && procedures && procedures.length > 0) {
      const defaultProcedureIndices = procedures
        .map((procedure) => availableProcedures
          .findIndex((currentProcedure) => procedure === currentProcedure))
        .filter((procedureIndex) => procedureIndex > -1);

      setSelectedProcedureIndices(defaultProcedureIndices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProcedures]);

  useEffect(() => {
    if (job) {
      setOfferStartDate(moment(`${job.local_date} ${job.local_start_time}`, 'ddd, MMM DD, YYYY hh:mm a').toDate());
      setOfferStartTime(moment(`${job.local_date} ${job.local_start_time}`, 'ddd, MMM DD, YYYY hh:mm a').toDate());
      setOfferEndTime(moment(`${job.local_date} ${job.local_end_time}`, 'ddd, MMM DD, YYYY hh:mm a').toDate());
      setOfferRate(job.rate.toFixed(2));
      setBreakTime(job.lunch_break_time);
      setIsBreakPaid(job.is_lunch_break_paid);
      setEnableAutoFill(job.autoFill);
    } else {
      const { jobId } = match.params;

      dispatch(fetchJob({ jobId }));
    }

    if (getShouldUsingCredentialServiceEndpoint()) {
      dispatch(
        getProfessionProcedures({
          profession,
          userType: UserType.DNT,
        }),
      );
    } else {
      dispatch(getUserEducation({ profession }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (content === 'start_time') {
      setOfferStartTime(resultFromModal);
    } else if (content === 'end_time') {
      setOfferEndTime(resultFromModal);
    }
  }, [resultFromModal]);

  const editOffer = () => {
    const localDate = moment(offerStartDate).format('yyyy-MM-DD');
    const jobData = {
      localDate,
      localStart: moment(offerStartTime).format('hh:mm a'),
      localEnd: moment(offerEndTime).format('hh:mm a'),
      rate: parseFloat(offerRate),
      lunch_break_time: parseInt(breakTime, 0),
      is_lunch_break_paid: isBreakPaid,
      autoFill: enableAutoFill,
      procedures: selectedProcedureIndices.map((index) => availableProcedures[index]),
    };

    dispatch(editJob({ jobId: job.id, jobData }));
  };

  const changeBreakTime = (event) => {
    setBreakTime(event.time);
    setIsBreakPaid(event.paid);
  };

  const showContent = (type) => {
    if (type === 'start_time' || type === 'end_time') {
      setContent(type);
      setModalIsOpen(true);
      setShowStartTime(true);
    } else if (type === 'break') {
      setContent(type);
      setResultFromModal(() => changeBreakTime);
      setModalIsOpen(true);
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setShowStartTime(false);
  };

  const startTime = (event) => {
    setOfferStartTime(moment(event, 'hh:mm aa'));
  };

  const endTime = (event) => {
    setOfferEndTime(moment(event, 'hh:mm aa'));
  };

  const offerRateChange = (event) => {
    setOfferRate(event);
  };

  const showTime = (type) => {
    setShowStartTime(true);
    if (type === 'start_time') {
      setResultFromModal(() => startTime);
    } else if (type === 'end_time') {
      setResultFromModal(() => endTime);
    }
    setContent(type);
  };

  const renderRow = (title, value, onClick, hasArrow, content) => (
    <div
      style={{
        maxWidth: 500,
        cursor: hasArrow ? 'pointer' : 'default',
        borderBottom: `1px solid ${Colors.neutral_100}`,
      }}
    >
      <div className="row_container" onClick={onClick}>
        <div
          className="row_container"
          style={{ width: '95%', cursor: hasArrow ? 'pointer' : 'default' }}>
          <p className="f-dark global_font bold f-18">{title}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <p className="f-dark global_font f-16">{value}</p>
          </div>
        </div>
        {hasArrow && (
          <img alt="arrow icon" src={arrow} style={{ marginLeft: 30 }} />
        )}
      </div>

      {content}
    </div>
  );

  let modalDefault = '';
  if (showStartTime) {
    modalDefault = content === 'start_time' ? offerStartTime : offerEndTime;
  }

  if (!job) {
    return (
      <div className="component_container">
        <div className="floating_container">
          <ClipLoader
            sizeUnit="px"
            size={50}
            color={Colors.primary_500}
            loading
          />
        </div>
      </div>
    );
  }

  return (
    <div className="component_container">
      <div className="floating_container">
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%',
        }}
        >
          <div style={{
            width: '100%', display: 'flex', marginLeft: 50, marginTop: 50,
          }}
          >
            <BackButton />
          </div>
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h1
                className="global_font"
                style={{
                  color: Colors.neutral_600,
                  fontSize: 32,
                  fontWeight: 700,
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                Edit Shift
              </h1>
              {renderRow(
                'Office:',
                job?.offer_owner?.office_name,
                () => {
                },
                false,
              )}

              <div
                className="row_container"
                onClick={() => setShowCalendarPopup(true)}
                style={{ width: '500px', cursor: 'pointer' }}
              >
                <div className="row_container" style={{
                  width: '95%',
                  borderBottom: `1px solid ${Colors.neutral_100}`,
                  cursor: 'pointer',
                }}>
                  <p className="f-dark global_font bold f-18">Date:</p>
                  <div
                    style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <p className="f-dark global_font f-16">
                      {' '}
                      {offerStartDate ? moment(offerStartDate).format('ddd, MMM DD, YYYY') : 'Date'}
                    </p>
                  </div>
                </div>
                <img alt="arrow icon" src={arrow} style={{ marginLeft: 30 }} />
              </div>

              {renderRow(
                'Profession:',
                UserSubProfession.getName(subProfession) ?? professionName,
                () => {
                },
                false,
              )}
              {renderRow(
                'Preferred Specialty:',
                specialty?.split('_')?.[1] || 'General',
                () => {
                },
                false,
              )}

              {
                getShouldAllowOfficeToPostProcedure() &&
                availableProcedures?.length > 0 && (
                  renderRow(
                    'Shift Procedure(s):',
                    '',
                    () => {
                    },
                    false,
                    (
                      <SelectableChips
                        style={{ marginBottom: 24 }}
                        values={EducationUtils.mapToRemoveProcedurePrefix(availableProcedures)}
                        selectedIndices={new Set(selectedProcedureIndices)}
                        onItemPressed={(index) => {
                          const indices = (() => {
                            const selected = selectedProcedureIndices.includes(index);
                            const collection = selectedProcedureIndices;
                            const maxSelection = 4;

                            if (selected) {
                              return collection
                                .filter((currentIndex) => currentIndex !== index);
                            }

                            if (!(maxSelection && collection.length >= maxSelection)) {
                              return [...collection, index];
                            }

                            return collection;
                          })();

                          setSelectedProcedureIndices(indices);
                        }}
                      />
                    ),
                  )
                )
              }

              {renderRow(
                'Arrival:',
                offerStartTime ? moment(offerStartTime).format('hh:mm a') : 'Time',
                () => showTime('start_time'),
                true,
              )}
              {renderRow(
                'Departure:',
                offerEndTime ? moment(offerEndTime).format('hh:mm a') : 'Time',
                () => showTime('end_time'),
                true,
              )}
              {renderRow(
                'Hourly Rate:',
                offerRate ? `$ ${offerRate}` : '$ ⏤.⏤',
                () => setShowRatePopup(true),
                true,
              )}
              {renderRow(
                `Break ${isBreakPaid ? '(Paid)' : '(Unpaid)'}:`,
                `${breakTime} min`,
                () => showContent('break'),
                true,
              )}
              <div className="row_container" style={{ width: '500px', cursor: 'default' }}>
                <div className="row_container" style={{
                  width: '95%',
                  borderBottom: `1px solid ${Colors.neutral_100}`,
                  cursor: 'default',
                }}>
                  <p className="f-dark global_font bold f-18">Auto-Confirm:</p>
                  <div
                    style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <ReactSwitch
                      checked={enableAutoFill}
                      onChange={() => setEnableAutoFill(!enableAutoFill)}
                      onColor={Colors.secondary_500}
                      onHandleColor="#fff"
                      handleDiameter={20}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      offColor={Colors.neutral_100}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
                      activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                      height={25}
                      width={50}
                      className="react-switch"
                      id="material-switch"
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 100, marginBottom: 50, alignSelf: 'center' }}>
                <Button
                  text="Save Changes"
                  textClassName="text-capitalize"
                  onClick={() => editOffer()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <MobileAppModal
        show={modalIsOpen}
        closeModal={closeModal}
        height={973}
        defaultValue={modalDefault}
        content={content}
        result={resultFromModal}
      />

      {showCalendarPopup ? (
        <CalendarPopup
          closeModal={() => setShowCalendarPopup(false)}
          confirmDateBtn={(date) => setOfferStartDate(date)}
        />
      ) : (
        <div />
      )}

      {showStartTime && (
        <ShowStartTime
          content={content}
          result={resultFromModal}
          closeModal={closeModal}
          height={973}
          defaultStartTime={offerStartTime}
          defaultEndTime={offerEndTime}
        />
      )}
      {showRatePopup && (
        <ModalRatePopup
          closeModal={() => setShowRatePopup(false)}
          submitRate={(value) => offerRateChange(value)}
          averagePrice={profRate}
          selectedProfession={job.profession}
          defaultValue={offerRate}
        />
      )}
    </div>
  );
};

export default EditJobPostFunction;
